import { useState } from "react";
import { T, UI } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { dateNumeric } from "../utils/formatDate";
import { useCompanyFinancials, CONCEPTS, NOT_REPORTED_WHY, NOT_REPORTED_GENERIC, lastYears, yearsInSpan } from "../utils/companyFinancials";
import Icon from "./Icon";
import LTR from "./LTR";
import { CompactMoney } from "./Money";
import SecEmpty from "./SecEmpty";
import { useSecStatus } from "../utils/secStatus";

// ─── נתונים פיננסיים — the company's reported figures, and nothing else ──────
// One row per concept: the latest reported value, the last five fiscal years
// as bars, and the filing it came from (form, filing date, the XBRL tag the
// company used, a copyable link to the filing index). A concept the company
// does not report is a row that says so and why — a bank without gross profit
// is how banks report, not a gap in the data.
//
// No ratios, no scores, no peers, no "good/bad". The bars are neutral ink:
// green and red belong to the user's own gain and loss, and a company's
// revenue moving is neither. Free cash flow is the one derived row and is
// labelled as such, with the two reported rows it came from.

// Text, for a title attribute and for a filing in a unit other than dollars.
function moneyText(n, unit = "USD") {
  if (n == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  const cur = unit === "USD" ? "$" : `${unit} `;
  if (abs >= 1e9) return `${sign}${cur}${(abs / 1e9).toFixed(abs >= 1e11 ? 0 : 1)}B`;
  if (abs >= 1e6) return `${sign}${cur}${(abs / 1e6).toFixed(abs >= 1e8 ? 0 : 1)}M`;
  if (abs >= 1e3) return `${sign}${cur}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}${cur}${abs.toFixed(0)}`;
}

// On screen, dollars go through the shared CompactMoney (sprint 17): the "$"
// and the minus follow the one rule. Any other unit stays as filed.
function money(n, unit = "USD", size = null) {
  if (n == null || !Number.isFinite(n)) return "—";
  if (unit !== "USD") return moneyText(n, unit);
  return <CompactMoney value={n} size={size} weight={null}/>;
}

// Five bars, scaled to the largest absolute value in the window. A negative
// year hangs below the baseline. Height carries the comparison; there is no
// axis and no number on the bars — the numbers are printed under them.
function Trend({ rows }) {
  const max = Math.max(...rows.filter(r => !r.missing).map(r => Math.abs(r.value)), 1);
  const H = 34;
  // Room below the baseline only when a year is actually negative; a series
  // of positive years keeps the whole height for the bars.
  const hasNeg = rows.some(r => r.value < 0);
  const total = hasNeg ? H * 2 : H;
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: total, position: "relative" }} aria-hidden="true">
      <div style={{ position: "absolute", left: 0, right: 0, top: H, height: 1, background: UI.line.strong }}/>
      {rows.map(r => {
        if (r.missing) return <div key={r.period_end} title={`${r.fiscal_year}: לא דווח`} style={{ flex: 1, minWidth: 8, height: total }}/>;
        const h = Math.max(2, Math.round((Math.abs(r.value) / max) * (H - 2)));
        const neg = r.value < 0;
        return (
          <div key={r.period_end} title={`${r.fiscal_year}: ${moneyText(r.value, r.unit)}`}
            style={{ flex: 1, minWidth: 8, height: total, position: "relative" }}>
            <div style={{
              position: "absolute", left: 0, right: 0,
              top: neg ? H : H - h, height: h,
              background: UI.text.secondary, opacity: .55, borderRadius: 2,
            }}/>
          </div>
        );
      })}
    </div>
  );
}

// The filing line: which report, when it was filed, and a link to it. The
// XBRL tag name (RevenueFromContractWithCustomerExcludingAssessedTax) used to
// be printed here · accurate, and meaningless to a reader (David, 25.09). It
// stays in the data; the screen names the document instead.
function FilingLine({ row }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, fontSize: 12, color: UI.text.muted, marginTop: 8 }}>
      <Icon name="clipboard" size={12} color={UI.text.muted}/>
      <span>מקור: <LTR style={{ color: UI.text.secondary, fontWeight: 700 }}>SEC EDGAR</LTR> · דוח <LTR style={{ color: UI.text.secondary, fontWeight: 700 }}>{row.form}</LTR></span>
      <span>·</span><span>הוגש <LTR className="si-num">{dateNumeric(row.filed_date)}</LTR></span>
      {row.source_url && (
        <>
          {/* No "·" before the link: when the row wraps it would open a
              line of its own, pointing at nothing. */}
          <a href={row.source_url} target="_blank" rel="noopener noreferrer"
            style={{ color: UI.interactive, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, minHeight: 32 }}>
            <Icon name="link" size={12} color={UI.interactive}/> לדוח ב-<LTR>SEC</LTR>
          </a>
        </>
      )}
    </div>
  );
}

// The source is stated once, at the top (2026-09-26). A row repeats it only
// when its own number came from a DIFFERENT filing than the latest one.
const sameFiling = (a, b) => !!a && !!b && a.form === b.form && a.filed_date === b.filed_date;

function ConceptRow({ def, rows, derived = false, top, otherYears }) {
  const last5 = lastYears(rows, 5);
  const latest = last5[last5.length - 1];
  const hiddenYears = yearsInSpan(otherYears, last5);
  return (
    <div style={{ padding: `${T.scale[3]}px 0`, borderBottom: `1px solid ${UI.line.hairline}` }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <span style={{ ...TYPE.bodySmall, fontWeight: 700, color: UI.text.primary }}>{def.label}</span>
          {derived && (
            <span style={{ marginInlineStart: 8, fontSize: 11.5, fontWeight: 700, color: UI.text.muted, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.pill, padding: "1px 8px" }}>
              נגזרת
            </span>
          )}
          <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 2 }}>
            {def.kind === "balance" ? "מאזן · ליום סיום השנה" : "לשנת הכספים"} <LTR>{latest.fiscal_year}</LTR>
          </div>
        </div>
        <LTR className="si-num" style={{ ...TYPE.value, fontSize: 18, color: UI.text.primary }}>{money(latest.value, latest.unit)}</LTR>
      </div>

      {/* The five years: bars, then the numbers the bars stand for. */}
      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr", gap: 6 }}>
        <Trend rows={last5}/>
        <div style={{ display: "flex", gap: 4 }}>
          {last5.map(r => (
            <div key={r.period_end} style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div className="si-num" style={{ fontSize: 11.5, color: UI.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><LTR>{r.missing ? "—" : money(r.value, r.unit)}</LTR></div>
              <div style={{ fontSize: 10.5, color: UI.text.muted }}><LTR>{r.fiscal_year}</LTR></div>
            </div>
          ))}
        </div>
      </div>

      {derived
        ? (
          <div style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.6, marginTop: 8 }}>
            תזרים מפעילות שוטפת פחות השקעות הוניות · חישוב משתי שורות מדווחות, לא סעיף שהחברה דיווחה.
            {/* One LTR run: two separate runs in an RTL line swapped places
                and read "$12.7B − $111B". */}
            {" "}<LTR style={{ whiteSpace: "nowrap" }}>{money(latest.from.cfo.value, latest.unit)} − {money(latest.from.capex.value, latest.unit)}</LTR>
          </div>
        )
        : !sameFiling(latest, top) && <FilingLine row={latest}/>}
      {hiddenYears.length > 0 && (
        <div style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.6, marginTop: 6 }}>
          שנים שהחברה דיווחה בהגדרה אחרת של הסעיף אינן מוצגות כאן: <LTR>{hiddenYears.join(", ")}</LTR>
        </div>
      )}
    </div>
  );
}

function NotReportedRow({ def, lastYear }) {
  return (
    <div style={{ padding: `${T.scale[3]}px 0`, borderBottom: `1px solid ${UI.line.hairline}` }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <span style={{ ...TYPE.bodySmall, fontWeight: 700, color: UI.text.muted }}>{def.label}</span>
        <span style={{ fontSize: 12.5, color: UI.text.muted }}>{lastYear ? <>דווח לאחרונה לשנת <LTR>{lastYear}</LTR></> : "לא דווח"}</span>
      </div>
      <div style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.6, marginTop: 4 }}>
        {lastYear ? "החברה לא דיווחה את הסעיף הזה בדוחות השנתיים האחרונים, ולכן המספר הישן אינו מוצג" : (NOT_REPORTED_WHY[def.id] || NOT_REPORTED_GENERIC)}
      </div>
    </div>
  );
}

// The four rows shown before "הצג עוד" (sprint 14, 4.2).
export const KEY_ROWS = ["revenue", "net_income", "fcf", "long_term_debt"];

export default function FinancialsPanel({ symbol }) {
  const data = useCompanyFinancials(symbol);
  const [open, setOpen] = useState(false);
  // Read only when there is nothing to show: what the SEC sync says decides
  // between "בטעינה", "לא הצלחנו" and "לא נמצאו" (21/10/2026).
  const hasRows = !!data?.company && CONCEPTS.some(c => (data.byConcept[c.id] || []).length > 0);
  const secStatus = useSecStatus(symbol, data !== null && !hasRows);

  if (data === null) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 32, gap: 10, color: UI.text.muted, fontSize: 13 }}>
        <span className="si-spin" style={{ width: 18, height: 18, border: `2px solid ${UI.line.hairline}`, borderTopColor: UI.interactive, borderRadius: "50%", display: "inline-block" }}/>
        טוען נתונים מדווחים...
      </div>
    );
  }

  const frame = { background: UI.surface.page, border: `1px solid ${UI.line.hairline}`, borderRadius: 6, padding: "18px 18px 12px" };

  if (!data.company) {
    return (
      <div dir="rtl" style={frame}>
        <SecEmpty symbol={symbol} status={secStatus}/>
      </div>
    );
  }

  const reported = CONCEPTS.filter(c => (data.byConcept[c.id] || []).length > 0);
  // Every row the panel can draw, the derived free cash flow among them.
  const FCF = { id: "fcf", label: "תזרים מזומנים חופשי", kind: "flow" };
  const items = [
    ...reported.map(def => ({ def, rows: data.byConcept[def.id], other: (data.otherDefinition || {})[def.id] })),
    ...(data.derived.fcf ? [{ def: FCF, rows: data.derived.fcf, derived: true }] : []),
  ];
  const key = KEY_ROWS.map(id => items.find(it => it.def.id === id)).filter(Boolean);
  const more = items.filter(it => !KEY_ROWS.includes(it.def.id));
  const row = it => <ConceptRow key={it.def.id} def={it.def} rows={it.rows} derived={!!it.derived} top={data.latestFiling} otherYears={it.other}/>;
  const missing  = CONCEPTS.filter(c => !(data.byConcept[c.id] || []).length);
  const stale = data.stale || {};

  if (!reported.length) {
    return (
      <div dir="rtl" style={frame}>
        <SecEmpty symbol={symbol} status={secStatus}/>
      </div>
    );
  }

  return (
    <div dir="rtl" style={frame}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: UI.text.secondary }}>נתונים מדווחים · דוחות שנתיים</span>
        {data.latestFiling && (
          <span style={{ fontSize: 12, color: UI.text.muted }}>הגשה אחרונה <LTR>{dateNumeric(data.latestFiling.filed_date)}</LTR></span>
        )}
      </div>
      <div style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.6, marginBottom: 6 }}>
        כפי שהחברה דיווחה ל-SEC בדוח השנתי · מספרים בלבד, בלי יחסים ובלי השוואות. ההגשה האחרונה לכל תקופה.
      </div>
      {data.latestFiling
        ? <FilingLine row={data.latestFiling}/>
        : null}

      {/* Four rows first (David, sprint 14, 4.2): revenue, net income, free
          cash flow and long-term debt, in that order, when reported. The
          rest, and what the company does not report, under "הצג עוד". */}
      {key.map(row)}
      {more.length + missing.length > 0 && (
        <button onClick={() => setOpen(o => !o)} aria-expanded={open}
          style={{ background: "transparent", border: "none", padding: "12px 0 4px", minHeight: 44, fontSize: 13.5, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit" }}>
          {open ? "הצג פחות" : <>הצג עוד · <LTR className="si-num">{more.length + missing.length}</LTR> סעיפים</>}
        </button>
      )}
      {open && more.map(row)}
      {open && missing.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: UI.text.muted, marginTop: 14, marginBottom: 2 }}>לא מדווח</div>
          {missing.map(def => <NotReportedRow key={def.id} def={def} lastYear={stale[def.id]}/>)}
        </>
      )}

    </div>
  );
}
